import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import "typeface-yanone-kaffeesatz";
import { useAuth } from "../context/AuthContext";
import { authFetch } from "../utils/authFetch";
import { BASE_URL } from "../utils/config";
import { useTranslation } from "react-i18next";
import styles from "./ProfilePage.module.css";
import SubscriptionModal from "../components/SubscriptionModal";
import eyeIcon from "../images/eye.svg";
import eyeOffIcon from "../images/eye-closed.svg";
import Autocomplete from 'react-google-autocomplete';
import LiveLocationToggle from '../components/LiveLocationToggle';

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

const getDisplayName = (currentUser) => {
  if (!currentUser) {
    return "Profile";
  }
  if (currentUser.name && currentUser.surname) {
    return `${currentUser.name} ${currentUser.surname}`;
  }
  if (currentUser.name) {
    return currentUser.sirname;
  }
  return "Profile";
};

const CustomModal = ({ message, onConfirm, onCancel, show, type }) => {
  // Auto-close alert modals after 3 seconds
  React.useEffect(() => {
    if (show && type === "alert") {
      const timer = setTimeout(() => {
        onCancel();
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [show, type, onCancel]);

  if (!show) return null;

  return (
    <div className={styles.modalOverlay} onClick={onCancel}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        {/* Close button (X) at top right */}
        <button className={styles.modalClose} onClick={onCancel} aria-label="Close">
          ×
        </button>
        <p className={styles.modalMessage}>{message}</p>
        <div className={styles.modalActions}>
          {type === "confirm" && (
            <>
              {/* Apply multiple classes using template literals */}
              <button
                className={`${styles.modalButton} ${styles.modalButtonRed}`}
                onClick={onConfirm}
              >
                Yes
              </button>
              <button className={styles.modalButton} onClick={onCancel}>
                No
              </button>
            </>
          )}
          {type === "alert" && (
            <button className={styles.modalButton} onClick={onCancel}>
              OK
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// const modalStyles = {
//   overlay: {
//     position: 'fixed',
//     top: 0,
//     left: 0,
//     right: 0,
//     bottom: 0,
//     backgroundColor: 'rgba(0, 0, 0, 0.5)',
//     display: 'flex',
//     justifyContent: 'center',
//     alignItems: 'center',
//     zIndex: 1000,
//   },
//   modal: {
//     backgroundColor: 'white',
//     padding: '30px',
//     borderRadius: '10px',
//     boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)',
//     textAlign: 'center',
//     maxWidth: '400px',
//     width: '90%',
//   },
//   message: {
//     fontSize: '18px',
//     marginBottom: '20px',
//     color: '#333',
//   },
//   actions: {
//     display: 'flex',
//     justifyContent: 'center',
//     gap: '15px',
//   },
//   button: {
//     padding: '10px 20px',
//     borderRadius: '5px',
//     border: 'none',
//     backgroundColor: '#79c3b2',
//     color: 'white',
//     fontSize: '16px',
//     cursor: 'pointer',
//     transition: 'background-color 0.3s ease',
//   },
// };

const ProfilePage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const { currentUser, logout, fetchAndUpdateUser } = useAuth();
  const [uploadedImageUrl, setUploadedImageUrl] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [profileImage, setProfileImage] = useState(null); // This state isn't strictly necessary if `uploadedImageUrl` directly updates the display.
  const [errors, setErrors] = useState({});
  const [profileData, setProfileData] = useState({
    name: "",
    surname: "",
    email: "",
    mobile: "",
    company: "",
    address: "",
    streetNumber: "",
    country: "",
    zip: "",
    addressCity: "",
    vatNumber: "",
    profilePicture: "",
    userId: "",
    totalMonthlyCount: 0,
    totalYearlyCount: 0,
    remainingMatchingCount: 0,
    subscriptionInterval: "", // Store the subscription interval (month or year)
    subscriptionPlanName: "", // Store the plan name
  });

  const [passwordData, setPasswordData] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });

  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [showModal, setShowModal] = useState(false);
  const [modalMessage, setModalMessage] = useState("");
  const [modalType, setModalType] = useState("alert");
  const modalCallback = useRef(null);
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);

  // Auto-refresh user data when page becomes visible (e.g., user returns from another tab)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden && currentUser) {
        console.log("👁️ Page became visible - refreshing user data");
        fetchAndUpdateUser();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [currentUser, fetchAndUpdateUser]);

  useEffect(() => {
    if (currentUser) {
      console.log(
        "ProfilePage: currentUser updated in useEffect:",
        currentUser
      );
      console.log(
        "ProfilePage: currentUser.userType in useEffect:",
        currentUser.userType
      );
      // Determine subscription interval and matching count
      const subscription = currentUser.UserSubscription;
      console.log("🔍 DEBUG - Full subscription object:", subscription);
      console.log("🔍 DEBUG - Subscription interval:", subscription?.interval);
      console.log("🔍 DEBUG - Subscription matchingCount:", subscription?.matchingCount);
      console.log("🔍 DEBUG - SubscriptionPlan:", subscription?.SubscriptionPlan);

      // Check if subscription is actually active
      const isSubscriptionActive =
        subscription &&
        subscription.status === "active" &&
        new Date(subscription.endDate) > new Date();

      // Only show matching counts if subscription is active
      const interval = isSubscriptionActive ? subscription?.interval : null;
      const matchingCount = isSubscriptionActive ? Number(subscription?.matchingCount) || 0 : 0;
      const usedMatchingCount = isSubscriptionActive ? Number(subscription?.usedMatchingCount) || 0 : 0;
      const planMatchingCount = isSubscriptionActive ? Number(subscription?.SubscriptionPlan?.matchingCount) || 0 : 0;
      const actualMatchingCount = Math.max(planMatchingCount, matchingCount);
      const remainingMatchingCount = Math.max(0, matchingCount - usedMatchingCount);

      const planInterval = subscription?.SubscriptionPlan?.interval;
      const planName = subscription?.SubscriptionPlan?.name || "";
      const effectiveInterval = interval || planInterval;

      console.log("🔍 DEBUG - Plan Name:", planName);
      console.log("🔍 DEBUG - Effective Interval:", effectiveInterval);
      console.log("🔍 DEBUG - Interval type:", typeof effectiveInterval);
      console.log("🔍 DEBUG - Is 'month'?", effectiveInterval === 'month');
      console.log("🔍 DEBUG - Is 'year'?", effectiveInterval === 'year');

      // If we still don't have an interval, try to infer from the matching count or default to month
      let finalInterval = effectiveInterval;
      if (!finalInterval && actualMatchingCount > 0) {
        // Default to month if we have a matching count but no interval
        finalInterval = 'month';
        console.warn("⚠️ No interval found, defaulting to 'month' for display purposes");
      }

      const monthlyCount = finalInterval === 'month' ? actualMatchingCount : 0;
      const yearlyCount = finalInterval === 'year' ? actualMatchingCount : 0;

      console.log("🔍 DEBUG - Final Interval (for display):", finalInterval);
      console.log("🔍 DEBUG - Monthly Count:", monthlyCount);
      console.log("🔍 DEBUG - Yearly Count:", yearlyCount);

      setProfileData({
        name: currentUser.name || "",
        surname: currentUser.surname || "",
        email: currentUser.email || "",
        mobile: currentUser.mobile || "",
        company: currentUser.companyName || "",
        address: currentUser.address || "",
        streetNumber: currentUser.streetNumber || "",
        country: currentUser.country || "",
        zip: currentUser.zipCode || "",
        addressCity: currentUser.city || "",
        vatNumber: currentUser.vatId || "",
        profilePicture: currentUser.profilePicture || "",
        userId: currentUser.id || "",
        matchingCount: actualMatchingCount,
        remainingMatchingCount,
        totalMonthlyCount: monthlyCount,
        totalYearlyCount: yearlyCount,
        subscriptionInterval: finalInterval || "", // Store the interval (with fallback)
        subscriptionPlanName: planName, // Store the plan name
      });
      console.log(
        "profileData set. New profileData state (will be available on next render):",
        {
          name: currentUser.name || "",
          surname: currentUser.surname || "",
          email: currentUser.email || "",
          mobile: currentUser.mobile || "",
          company: currentUser.companyName || "",
          country: currentUser.country || "",
          zip: currentUser.zipCode || "",
          addressCity: currentUser.city || "",
          vatNumber: currentUser.vatId || "",
          profilePicture: currentUser.profilePicture || "",
          userId: currentUser.id || "",
          subscriptionPlanName: planName,
          subscriptionInterval: interval,
          planInterval: planInterval,
          effectiveInterval: effectiveInterval,
          finalInterval: finalInterval,
          matchingCount: actualMatchingCount,
          totalMonthlyCount: monthlyCount,
          totalYearlyCount: yearlyCount,
        }
      );
      // Set profileImage for display if a picture exists
      setProfileImage(currentUser.profilePicture);
    } else {
      console.log("ProfilePage: currentUser is null, redirecting to login.");
      navigate("/");
    }
  }, [currentUser, navigate]);

  const handleProfileChange = (e) => {
    const { name, value } = e.target;
    setProfileData({
      ...profileData,
      [name]: value,
    });
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordData({
      ...passwordData,
      [name]: value,
    });
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Profile validation
    if (!profileData.name.trim()) newErrors.name = "Name is required"; // Corrected from firstName
    if (!profileData.surname.trim()) newErrors.surname = "Surname is required"; // Corrected from lastName
    if (!profileData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/^\S+@\S+\.\S+$/.test(profileData.email)) {
      newErrors.email = "Email is invalid";
    }
    if (!profileData.country.trim()) newErrors.country = "Country is required";
    if (!profileData.addressCity.trim())
      newErrors.addressCity = "City is required";

    // Password validation if any password field is filled
    if (
      passwordData.currentPassword ||
      passwordData.newPassword ||
      passwordData.confirmPassword
    ) {
      if (!passwordData.currentPassword)
        newErrors.currentPassword = "Current password is required";
      if (!passwordData.newPassword) {
        newErrors.newPassword = "New password is required";
      } else if (passwordData.newPassword.length < 8) {
        newErrors.newPassword = "Password must be at least 8 characters";
      }
      if (passwordData.newPassword !== passwordData.confirmPassword) {
        newErrors.confirmPassword = "Passwords do not match";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const showCustomModal = (
    message,
    type = "alert",
    onConfirmCallback = null
  ) => {
    setModalMessage(message);
    setModalType(type);
    modalCallback.current = onConfirmCallback;
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setModalMessage("");
    setModalType("alert");
    modalCallback.current = null;
  };

  const handleModalConfirm = () => {
    if (modalCallback.current) {
      modalCallback.current();
    }
    closeModal();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (validateForm()) {
      console.log("Profile updated:", profileData);

      // If password change is requested, handle it directly
      if (passwordData.newPassword) {
        await changePassword();
        return;
      }

      // Update profile information
      await updateProfile();
    }
  };

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    
    // Validate only profile fields, not password fields
    const newErrors = {};
    
    if (!profileData.name.trim()) newErrors.name = "Name is required";
    if (!profileData.surname.trim()) newErrors.surname = "Surname is required";
    if (!profileData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/^\S+@\S+\.\S+$/.test(profileData.email)) {
      newErrors.email = "Email is invalid";
    }
    if (!profileData.country.trim()) newErrors.country = "Country is required";
    if (!profileData.addressCity.trim()) newErrors.addressCity = "City is required";
    
    setErrors(newErrors);
    
    if (Object.keys(newErrors).length === 0) {
      await updateProfile();
    }
  };

  const changePassword = async () => {
    // Validate password fields
    const newErrors = {};

    if (!passwordData.currentPassword) {
      newErrors.currentPassword = "Current password is required";
    }
    if (!passwordData.newPassword) {
      newErrors.newPassword = "New password is required";
    } else if (passwordData.newPassword.length < 8) {
      newErrors.newPassword = "Password must be at least 8 characters";
    }
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      newErrors.confirmPassword = "Passwords do not match";
    }

    setErrors(newErrors);

    if (Object.keys(newErrors).length > 0) {
      return;
    }

    try {
      const response = await authFetch(`${BASE_URL}/users/change-password`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          oldPassword: passwordData.currentPassword,
          newPassword: passwordData.newPassword,
        }),
      });

      const data = await response.json();

      if (response.ok) {
        showCustomModal("Password changed successfully!", "alert", () => {
          // Clear password fields and hide password visibility
          setPasswordData({
            currentPassword: "",
            newPassword: "",
            confirmPassword: "",
          });
          setShowCurrentPassword(false);
          setShowNewPassword(false);
          setShowConfirmPassword(false);
        });
      } else {
        showCustomModal(
          data.message || "Failed to change password. Please try again.",
          "alert"
        );
      }
    } catch (error) {
      console.error("Error changing password:", error);
      showCustomModal("Error changing password. Please try again.", "alert");
    }
  };

  const updateProfile = async () => {
    try {
      const response = await authFetch(`${BASE_URL}/user/profile`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(profileData),
      });

      const data = await response.json();

      if (response.ok) {
        // Refresh user data from backend to update AuthContext and localStorage
        await fetchAndUpdateUser();
        showCustomModal("Profile updated successfully!", "alert");
      } else {
        showCustomModal(
          data.message || "Failed to update profile. Please try again.",
          "alert"
        );
      }
    } catch (error) {
      console.error("Error updating profile:", error);
      showCustomModal("Error updating profile. Please try again.", "alert");
    }
  };

  const handleDeleteProfile = () => {
    if (!currentUser || !currentUser.id) {
      showCustomModal("User ID not available. Cannot delete profile.", "alert");
      return;
    }

    showCustomModal(
      "Are you sure you want to delete your profile? This action cannot be undone.",
      "confirm",
      async () => {
        console.log(
          `Attempting to soft delete profile for user ID: ${currentUser.id}`
        );
        try {
          const response = await authFetch(`${BASE_URL}/delete-user`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({ userId: currentUser.id }),
          });

          const data = await response.json();

          if (response.ok) {
            console.log("Profile soft deleted successfully on backend:", data);
            showCustomModal("Profile deleted successfully", "alert", () => {
              logout();
              navigate("/");
            });
          } else {
            console.error(
              "Failed to soft delete profile:",
              data.message || response.statusText
            );
            showCustomModal(
              data.message || "Failed to delete profile. Please try again.",
              "alert"
            );
          }
        } catch (error) {
          console.error("Network error during profile deletion:", error);
          showCustomModal(
            "Network error. Could not connect to the server.",
            "alert"
          );
        }
      }
    );
  };

  const handleSignOut = () => {
    console.log("User signed out");
    logout();
    navigate("/");
  };

  const handleProfileButtonClick = () => {
    fileInputRef.current.click();
  };

  // `triggerFileInput` is redundant with `handleProfileButtonClick`
  // const triggerFileInput = () => {
  //     fileInputRef.current.click();
  // };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        // 2MB limit
        showCustomModal(t("image-size-limit-error"), "alert");
        e.target.value = null;
        return;
      }
      if (!file.type.startsWith("image/")) {
        showCustomModal(t("only-images-allowed"), "alert");
        e.target.value = null;
        return;
      }

      const reader = new FileReader();
      reader.onloadend = () => {
        setProfileImage(reader.result); // Set for immediate display
      };
      reader.readAsDataURL(file);

      uploadImageToDigitalOceanSpaces(file);
    }
  };

  const uploadImageToDigitalOceanSpaces = async (file) => {
    setIsUploading(true);
    try {
      // Use FormData to upload directly through backend (avoids CORS issues)
      const formData = new FormData();
      formData.append('files', file);
      formData.append('profileImage', 'true');

      const response = await authFetch(
        `${BASE_URL}/media/generate-upload-url`,
        {
          method: "POST",
          body: formData, // Send as FormData instead of JSON
        }
      );

      const result = await response.json();

      if (!response.ok) {
        showCustomModal(result.message || t("failed-get-upload-url"), "error");
        setIsUploading(false);
        return;
      }

      // Backend returns uploadedFiles array when using multipart upload
      const uploadedFiles = result.uploadedFiles;

      if (!uploadedFiles || uploadedFiles.length === 0) {
        showCustomModal(t("backend-no-valid-urls"), "error");
        setIsUploading(false);
        return;
      }

      const finalPublicUrl = uploadedFiles[0].url;

      console.log(
        "Image uploaded successfully to DigitalOcean Spaces:",
        finalPublicUrl
      );
      setUploadedImageUrl(finalPublicUrl); // Set for immediate display

      // Update the user's profile in backend with the new profile picture URL
      try {
        const updateProfileResponse = await authFetch(
          `${BASE_URL}/user/profile`,
          {
            method: "PUT",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              profilePicture: finalPublicUrl,
              userId: currentUser.id,
            }),
          }
        );

        const updateProfileResult = await updateProfileResponse.json();

        if (updateProfileResponse.ok) {
          console.log(
            "User profile updated in backend with new picture:",
            updateProfileResult
          );
          
          // Refresh user data from backend to update AuthContext and localStorage
          await fetchAndUpdateUser();
          
          showCustomModal(
            t("image-upload-success-and-profile-updated"),
            "success"
          );
        } else {
          console.error(
            "Failed to update user profile with new picture:",
            updateProfileResult.message || updateProfileResponse.statusText
          );
          showCustomModal(
            updateProfileResult.message || t("failed-update-profile-picture"),
            "error"
          );
        }
      } catch (updateError) {
        console.error("Error updating user profile in backend:", updateError);
        showCustomModal(t("error-updating-profile-backend"), "error");
      }
    } catch (error) {
      console.error("Error during image upload process:", error);
      showCustomModal(t("error-during-upload"), "error");
    } finally {
      setIsUploading(false);
    }
  };
  console.log(
    "ProfilePage: Rendering. currentUser?.userType:",
    currentUser?.userType
  );

  return (
    <div className={styles.container}>
      <CustomModal
        show={showModal}
        message={modalMessage}
        type={modalType}
        onConfirm={handleModalConfirm}
        onCancel={closeModal}
      />

      <div className={styles.profileCard}>
        <div className={styles.profileHeader}>
          <div className={styles.profileImageContainer}>
            <img
              src={
                profileImage ||
                "https://placehold.co/150x150/EEEEEE/333333?text=Profile"
              } // Use profileImage state for display
              alt="Profile"
              className={styles.profileImage}
            />
            <button
              onClick={handleProfileButtonClick}
              className={styles.uploadButton}
              disabled={isUploading}
            >
              {isUploading ? (
                t("uploading")
              ) : (
                <>
                  {" "}
                  {/* Use a fragment to wrap multiple elements */}
                  <i className="fa-solid fa-camera"></i> {t("Change Photo")}{" "}
                  {/* Added text here */}
                </>
              )}
            </button>
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleImageUpload}
              style={{ display: "none" }} // Keep this inline for hidden input
              accept="image/*"
            />
          </div>
          <h2 className={styles.profileName}>{getDisplayName(currentUser)}</h2>
        </div>

        <form onSubmit={handleSubmit}>
          <div className={styles.section}>
            <h3 className={styles.sectionTitle}>{t("My-Profile")}</h3>
            <div className={styles.formGrid}>
              <div className={styles.formGroup}>
                <label className={styles.label}>{t("name")}:</label>
                <input
                  type="text"
                  name="name" // Corrected name attribute
                  value={profileData.name}
                  onChange={handleProfileChange}
                  className={styles.input}
                />
                {errors.name && (
                  <span className={styles.error}>{errors.name}</span>
                )}
              </div>
              <div className={styles.formGroup}>
                <label className={styles.label}>{t("surname")}:</label>
                <input
                  type="text"
                  name="surname" // Corrected name attribute
                  value={profileData.surname}
                  onChange={handleProfileChange}
                  className={styles.input}
                />
                {errors.surname && (
                  <span className={styles.error}>{errors.surname}</span>
                )}
              </div>
              <div className={styles.formGroup}>
                <label className={styles.label}>{t("email")}:</label>
                <input
                  type="email"
                  name="email"
                  value={profileData.email}
                  onChange={handleProfileChange}
                  className={styles.input}
                  disabled
                />
                {errors.email && (
                  <span className={styles.error}>{errors.email}</span>
                )}
              </div>
              <div className={styles.formGroup}>
                <label className={styles.label}>{t("Mobile")}:</label>
                <input
                  type="text"
                  name="mobile"
                  value={profileData.mobile}
                  onChange={handleProfileChange}
                  className={styles.input}
                  disabled
                />
              </div>

              <div className={styles.formGroup}>
                <label className={styles.label}>{t("Address")} *:</label>
                <Autocomplete
                  apiKey={GOOGLE_MAPS_API_KEY}
                  onPlaceSelected={(place, inputRef) => {
                    const addressComponents = place?.address_components || [];
                    const getComponent = (type) => {
                      const component = addressComponents.find((comp) =>
                        comp.types.includes(type)
                      );
                      return component ? component.long_name : "";
                    };

                    const streetName = getComponent("route");
                    const streetNumber = getComponent("street_number");
                    const fullAddress = streetNumber ? `${streetName} ${streetNumber}` : streetName;

                    // Update the input field to show only street address
                    if (inputRef) {
                      inputRef.value = fullAddress;
                    }

                    setProfileData(prev => ({
                      ...prev,
                      address: fullAddress,
                      streetNumber: streetNumber,
                      addressCity: getComponent("locality") || getComponent("administrative_area_level_2"),
                      zip: getComponent("postal_code"),
                      country: getComponent("country")
                    }));
                  }}
                  options={{
                    types: ["address"],
                    componentRestrictions: { country: ["ch"] },
                  }}
                  className={styles.input}
                  placeholder={t("Start typing your address...")}
                  defaultValue={profileData.address || ""}
                />
              </div>

              <div className={styles.formGroup}>
                <label className={styles.label}>{t("city")} *:</label>
                <input
                  type="text"
                  name="addressCity"
                  value={profileData.addressCity}
                  onChange={handleProfileChange}
                  className={styles.input}
                  disabled
                  required
                />
                {errors.addressCity && (
                  <span className={styles.error}>{errors.addressCity}</span>
                )}
              </div>

              <div className={styles.formGroup}>
                <label className={styles.label}>{t("zip-code")} *:</label>
                <input
                  type="text"
                  name="zip"
                  value={profileData.zip}
                  onChange={handleProfileChange}
                  className={styles.input}
                  required
                />
              </div>

              <div className={styles.formGroup}>
                <label className={styles.label}>{t("country")} *:</label>
                <input
                  type="text"
                  name="country"
                  value={profileData.country}
                  onChange={handleProfileChange}
                  className={styles.input}
                  disabled
                  required
                />
                {errors.country && (
                  <span className={styles.error}>{errors.country}</span>
                )}
              </div>
              {/* <div className={styles.formGroup}>
            <label className={styles.label}>{t('Matching Count')}:</label>
            <input
                type="text"
                name="matchingCount"
                value={profileData.matchingCount ?? t('N/A')}
                readOnly
                className={styles.input}
            />
        </div> */}

              {currentUser && currentUser.userType === "offer" && (
                <>
                  <div className={styles.formGroup}>
                    <label className={styles.label}>{t("company")}:</label>
                    <input
                      type="text"
                      name="company"
                      value={profileData.company}
                      onChange={handleProfileChange}
                      className={styles.input}
                    />
                  </div>
                  <div className={styles.formGroup}>
                    <label className={styles.label}>{t("vat")}:</label>
                    <input
                      type="text"
                      name="vatNumber"
                      value={profileData.vatNumber}
                      onChange={handleProfileChange}
                      className={styles.input}
                    />
                  </div>

                  {/* Show only monthly count if subscription is monthly */}
                  {(profileData.subscriptionInterval === 'month' || (!profileData.subscriptionInterval && profileData.totalMonthlyCount > 0)) && (
                    <div className={styles.formGroup}>
                      <label className={styles.label}>{t("Monthly Count")}:</label>
                      <input
                        type="text"
                        name="totalMonthlyCount"
                        value={profileData.totalMonthlyCount}
                        readOnly
                        className={styles.input}
                        title="Your monthly subscription matching count"
                      />
                    </div>
                  )}

                  {/* Show only yearly count if subscription is yearly */}
                  {(profileData.subscriptionInterval === 'year' || (!profileData.subscriptionInterval && profileData.totalYearlyCount > 0)) && (
                    <div className={styles.formGroup}>
                      <label className={styles.label}>{t("Yearly Count")}:</label>
                      <input
                        type="text"
                        name="totalYearlyCount"
                        value={profileData.totalYearlyCount}
                        readOnly
                        className={styles.input}
                        title="Your yearly subscription matching count"
                      />
                    </div>
                  )}

                  <div className={styles.formGroup}>
                    <label className={styles.label}>{t("Remaining matches")}:</label>
                    <div style={{ display: 'flex', gap: '5px', alignItems: 'center' }}>
                      <input
                        type="text"
                        name="remainingMatchingCount"
                        value={profileData.remainingMatchingCount}
                        readOnly
                        className={styles.input}
                        title="How many matches you have left this period"
                      />
                      <button
                        type="button"
                        onClick={async () => {
                          console.log("🔄 Manually refreshing user data...");
                          await fetchAndUpdateUser();
                        }}
                        className={styles.refreshButton}
                        title="Refresh matching count"
                        style={{ padding: '8px', minWidth: '35px', height: '35px' }}
                      >
                        🔄
                      </button>
                    </div>
                  </div>
                  {/* <div className={styles.formGroup}>
    <label className={styles.label}>{t('Matching Count')}:</label>
    <input
        type="text"
        name="matchingCount"
        value={profileData.matchingCount ?? t('N/A')} // Display 'N/A' or 'Loading...' if null
        readOnly // Make it read-only as it's from API, not editable
        className={styles.input}
    />
</div> */}
                </>
              )}
            </div>
            
            {/* Update Profile Button */}
            <div className={styles.profileButtonContainer}>
              <button 
                type="button" 
                onClick={handleUpdateProfile} 
                className={styles.updateProfileButton}
              >
                {t("Update Profile")}
              </button>
            </div>
          </div>

          <div className={styles.section}>
            <h3 className={styles.sectionTitle}>{t("change-password")}</h3>
            <div className={styles.formGrid}>
              <div className={styles.formGroup}>
                <label className={styles.label}>{t("Current Password")}:</label>
                <div className={styles.passwordInputWrapper}>
                  <input
                    type={showCurrentPassword ? "text" : "password"}
                    name="currentPassword"
                    value={passwordData.currentPassword}
                    onChange={handlePasswordChange}
                    className={styles.input}
                  />
                  {passwordData.currentPassword && (
                    <img
                      src={showCurrentPassword ? eyeOffIcon : eyeIcon}
                      alt="Toggle password visibility"
                      onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                      className={styles.passwordToggle}
                    />
                  )}
                </div>
                {errors.currentPassword && (
                  <span className={styles.error}>{errors.currentPassword}</span>
                )}
              </div>
              <div className={styles.formGroup}>
                <label className={styles.label}>{t("New Password")}:</label>
                <div className={styles.passwordInputWrapper}>
                  <input
                    type={showNewPassword ? "text" : "password"}
                    name="newPassword"
                    value={passwordData.newPassword}
                    onChange={handlePasswordChange}
                    className={styles.input}
                  />
                  {passwordData.newPassword && (
                    <img
                      src={showNewPassword ? eyeOffIcon : eyeIcon}
                      alt="Toggle password visibility"
                      onClick={() => setShowNewPassword(!showNewPassword)}
                      className={styles.passwordToggle}
                    />
                  )}
                </div>
                {errors.newPassword && (
                  <span className={styles.error}>{errors.newPassword}</span>
                )}
              </div>
              <div className={styles.formGroup}>
                <label className={styles.label}>{t("Confirm Password")}:</label>
                <div className={styles.passwordInputWrapper}>
                  <input
                    type={showConfirmPassword ? "text" : "password"}
                    name="confirmPassword"
                    value={passwordData.confirmPassword}
                    onChange={handlePasswordChange}
                    className={styles.input}
                  />
                  {passwordData.confirmPassword && (
                    <img
                      src={showConfirmPassword ? eyeOffIcon : eyeIcon}
                      alt="Toggle password visibility"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className={styles.passwordToggle}
                    />
                  )}
                </div>
                {errors.confirmPassword && (
                  <span className={styles.error}>{errors.confirmPassword}</span>
                )}
              </div>
            </div>

            {/* Update Password Button */}
            <div className={styles.profileButtonContainer}>
              <button
                type="button"
                onClick={changePassword}
                className={styles.updatePasswordButton}
              >
                {t("Update Password")}
              </button>
            </div>
          </div>

          {currentUser && currentUser.userType === "offer" && (
            <div className={styles.section}>
              <h3 className={styles.sectionTitle}>{t("subscription")}</h3>
              <div className={styles.formGrid}>
                <div className={styles.formGroup}>
                  <label className={styles.label}>{t("Current Plan")}:</label>
                  <input
                    type="text"
                    value={
                      currentUser.UserSubscription &&
                      currentUser.UserSubscription.status === "active" &&
                      new Date(currentUser.UserSubscription.endDate) > new Date() &&
                      currentUser.UserSubscription.SubscriptionPlan?.name
                        ? currentUser.UserSubscription.SubscriptionPlan.name
                        : currentUser.UserSubscription &&
                          currentUser.UserSubscription.status === "active" &&
                          new Date(currentUser.UserSubscription.endDate) > new Date()
                        ? t("Active Subscription")
                        : t("No Subscription")
                    }
                    className={styles.input}
                    readOnly
                  />
                </div>
                <div className={styles.formGroup}>
                  <label className={styles.label}>{t("Action")}:</label>
                  <button
                    type="button"
                    className={styles.editSubscriptionButton}
                    onClick={() => setShowSubscriptionModal(true)}
                  >
                    {t("Edit Subscription Plan")}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Location Features - Only for Demand Users */}
          {/* {currentUser && currentUser.userType === "demand" && (
            <div className={styles.section}>
              <LiveLocationToggle />
            </div>
          )} */}

          <div className={styles.buttonGroup}>
            <button
              type="button"
              onClick={handleDeleteProfile}
              className={styles.deleteButton}
            >
              {t("delete_cta")}
            </button>
            <button
              type="button"
              onClick={handleSignOut}
              className={styles.signOutButton}
            >
              {t("Sign Out")}
            </button>
          </div>
        </form>
      </div>

      {/* Subscription Modal */}
      <SubscriptionModal
        isOpen={showSubscriptionModal}
        onClose={() => setShowSubscriptionModal(false)}
        currentUser={currentUser}
        onSubscriptionUpdate={fetchAndUpdateUser}
      />
    </div>
  );
};

// Remove the `styles` object as it's now in the CSS file
// const styles = { ... };

export default ProfilePage;
